<template>
  <div class="login-page-wrap">
    <div class="login-card-wrap">
      <b-card>
        <!-- Header -->
        <div class="text-center mb-2">
          <feather-icon
            :icon="headerIcon"
            size="40"
            class="text-primary mb-1"
          />
          <h5 class="mb-0">
            {{ headerTitle }}
          </h5>
          <p
            class="text-muted mb-0"
            style="font-size: 0.875rem;"
          >
            {{ headerSubtitle }}
          </p>
        </div>

        <b-form
          v-if="requiresReverification"
          @submit.prevent="verifyReverificationCode"
        >
          <p class="text-muted mb-1">
            We need to verify your email address. Enter the verification code
            sent to
            <span v-if="maskedEmail">{{ maskedEmail }}</span><span v-else>your registered email address</span>
            to continue.
          </p>

          <b-form-group label="Verification code">
            <b-form-input
              v-model="verificationCode"
              inputmode="numeric"
              maxlength="6"
              placeholder="Enter verification code"
              required
            />
          </b-form-group>

          <p
            v-if="reverificationExpired"
            class="text-danger mb-1"
          >
            Verification code has expired. Please request a new code.
          </p>

          <b-button
            type="submit"
            variant="primary"
            block
            :disabled="!isVerificationCodeComplete"
          >
            Verify
          </b-button>

          <b-button
            type="button"
            variant="outline-primary"
            block
            class="mt-1"
            :disabled="resendingReverificationCode || reverificationResendCooldown"
            @click="resendReverificationCode"
          >
            Resend verification code
          </b-button>

          <b-button
            type="button"
            variant="outline-secondary"
            block
            class="mt-1"
            @click="backToLogin"
          >
            Back to login
          </b-button>
        </b-form>

        <b-form
          v-else
          @submit.prevent="login"
        >
          <!-- Username -->
          <b-form-group class="mb-1">
            <b-input-group class="input-group-merge">
              <b-input-group-prepend is-text>
                <feather-icon icon="UserIcon" />
              </b-input-group-prepend>
              <b-form-input
                v-model="username"
                placeholder="Username"
                required
              />
            </b-input-group>
          </b-form-group>

          <!-- Password -->
          <b-form-group class="mb-1">
            <b-input-group class="input-group-merge">
              <b-input-group-prepend is-text>
                <feather-icon icon="KeyIcon" />
              </b-input-group-prepend>
              <b-form-input
                v-model="password"
                type="password"
                placeholder="Password"
                required
              />
            </b-input-group>
          </b-form-group>

          <b-form-group>
            <b-form-checkbox v-model="remember">
              Remember me
            </b-form-checkbox>
          </b-form-group>

          <!-- Submit button -->
          <b-button
            type="submit"
            variant="primary"
            block
          >
            Log In
          </b-button>

          <div class="mt-2 text-center">
            <b-link :to="{ name: 'forgot-password' }">
              Forgot password?
            </b-link>
          </div>

          <hr>

          <div class="text-center">
            <span class="text-muted">Don't have an account? </span>
            <b-link :to="{ name: 'register' }">
              Register
            </b-link>
          </div>
        </b-form>
      </b-card>
    </div>
  </div>
</template>

<script>
import {
  BCard, BForm, BFormGroup, BFormInput, BInputGroup, BInputGroupPrepend, BButton, BFormCheckbox, BLink,
} from 'bootstrap-vue'

export default {
  components: {
    BCard,
    BForm,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupPrepend,
    BFormCheckbox,
    BButton,
    BLink,
  },
  props: {
    hasStatic: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      username: '',
      password: '',
      remember: false,
      requiresReverification: false,
      maskedEmail: '',
      verificationCode: '',
      reverificationExpired: false,
      resendingReverificationCode: false,
      reverificationResendCooldown: false,
    }
  },
  computed: {
    headerIcon() {
      if (this.requiresReverification) return 'MailIcon'
      if (this.hasStatic) return 'LockIcon'
      return 'LogInIcon'
    },
    headerTitle() {
      if (this.requiresReverification) return 'Verify your email address'
      if (this.hasStatic) return 'Login required'
      return 'Welcome back'
    },
    headerSubtitle() {
      if (this.requiresReverification) {
        return ''
      }
      if (this.hasStatic) return 'Please log in to access this page'
      return 'Sign in to your account to continue'
    },
    isVerificationCodeComplete() {
      return /^[0-9]{6}$/.test(this.verificationCode)
    },
  },
  deactivated() {
    this.resetReverificationState()
  },
  beforeDestroy() {
    this.resetReverificationState()
  },
  methods: {
    login() {
      this.$http.post('auth.cfc?method=attemptLogin', new URLSearchParams({
        username: this.username,
        password: this.password,
        remember: this.remember,
      }))
        .then(response => {
          if (response.data.validUser) {
            if (response.data.requiresReverification) {
              this.requiresReverification = true
              this.maskedEmail = response.data.maskedEmail || response.data.MASKEDEMAIL || ''
              this.verificationCode = ''
              this.reverificationExpired = false
              this.reverificationResendCooldown = false
              this.password = ''
              return
            }

            this.completeLogin()
          } else {
            this.$bvToast.toast(response.data.errormessage, {
              title: 'Login Unsuccessful',
              variant: 'danger',
              solid: true,
            })
          }
        })
        .catch(error => {
          console.log(error)
        })
    },
    verifyReverificationCode() {
      this.$http.post('auth.cfc?method=verifyReverificationCode', new URLSearchParams({
        verificationCode: this.verificationCode,
      }))
        .then(response => {
          if (response.data.validUser) {
            this.requiresReverification = false
            this.maskedEmail = ''
            this.verificationCode = ''
            this.reverificationExpired = false
            this.completeLogin()
          } else {
            this.reverificationExpired = response.data.codeExpired || response.data.CODEEXPIRED || false
            this.$bvToast.toast(response.data.errormessage, {
              title: 'Verification Unsuccessful',
              variant: 'danger',
              solid: true,
            })
          }
        })
        .catch(error => {
          console.log(error)
        })
    },
    resendReverificationCode() {
      if (this.resendingReverificationCode || this.reverificationResendCooldown) return

      this.resendingReverificationCode = true
      this.reverificationResendCooldown = true
      this.$http.post('auth.cfc?method=resendReverificationCode')
        .then(response => {
          if (response.data.success || response.data.SUCCESS) {
            this.verificationCode = ''
            this.reverificationExpired = false
            this.$bvToast.toast('A new verification email has been sent', {
              title: 'Check your email',
              variant: 'success',
              solid: true,
            })
          } else {
            this.$bvToast.toast(response.data.errormessage, {
              title: 'Unable to resend code',
              variant: 'danger',
              solid: true,
            })
          }
          this.resendingReverificationCode = false
        })
        .catch(error => {
          console.log(error)
          this.resendingReverificationCode = false
        })
      setTimeout(() => {
        this.reverificationResendCooldown = false
      }, 5000)
    },
    completeLogin() {
      this.$store.dispatch('auth/getUserMetadata')
        .then(() => {
          if (this.$route.path.toLowerCase() === '/login') {
            if ('target' in this.$route.query) {
              window.location.replace(this.$route.query.target)
            } else {
              this.$router.push({ name: 'home' })
            }
          }
        })
    },
    resetReverificationState() {
      this.requiresReverification = false
      this.maskedEmail = ''
      this.verificationCode = ''
      this.reverificationExpired = false
      this.resendingReverificationCode = false
      this.reverificationResendCooldown = false
    },
    backToLogin() {
      this.resetReverificationState()
    },
  },
}
</script>

<style scoped>
.login-page-wrap {
  display: flex;
  justify-content: center;
  padding-top: 4rem;
}

.login-card-wrap {
  width: 460px;
  max-width: 90vw;
}

.login-card-wrap::v-deep .card-body {
  padding: 2rem;
}

.login-card-wrap::v-deep input:-webkit-autofill,
.login-card-wrap::v-deep input:-webkit-autofill:hover,
.login-card-wrap::v-deep input:-webkit-autofill:focus {
  -webkit-box-shadow: 0 0 0 1000px transparent inset;
  box-shadow: 0 0 0 1000px transparent inset;
  -webkit-text-fill-color: #6e6b7b;
  transition: background-color 5000s ease-in-out 0s;
}
</style>

<style>
.dark-layout input:-webkit-autofill,
.dark-layout input:-webkit-autofill:hover,
.dark-layout input:-webkit-autofill:focus {
  -webkit-text-fill-color: #b4b7bd !important;
}
</style>
